<script setup>
import { ref } from 'vue'

defineProps({
  modelValue: {
    required: true
  },
  id: String,
  prefix: String,
  suffix: String,
  type: String,
  label: String,
  min: String,
  step: String,
  maxlength: String,
  placeholder: String,
  autocomplete: String,
  required: Boolean,
  disabled: Boolean,
  autofocus: Boolean,
  pending: Boolean,
  pendingValue: String
})

const input = ref(null)

const focus = () => {
  input.value?.focus()
}

defineExpose({
  focus
})
</script>

<template>
  <div class="flex flex-col gap-2 rounded border p-2 overflow-hidden" :class="[disabled ? 'bg-gray-200 ' : 'bg-white']">
    <slot>
      <label
        v-if="label"
        :for="label"
        class="flex gap-1 text-xs font-light"
        :class="[disabled ? 'opacity-50' : 'opacity-75']"
      >
        <span>{{ label }}</span>
        <span v-if="!required" class="lowercase">({{ $t('labels.optional') }})</span>
      </label>
    </slot>
    <div class="flex items-center w-full">
      <div v-if="prefix && !pending" class="opacity-50 mr-2">
        <div class="whitespace-nowrap">{{ prefix }}</div>
      </div>
      <div v-if="pending" class="cursor-wait disabled pointer-events-none opacity-50">
        {{ pendingValue }}
      </div>
      <input
        v-else
        :id="id || label"
        ref="input"
        class="w-full font-normal !p-0"
        :value="modelValue"
        :required
        :type
        :min
        :maxlength
        :placeholder
        :autocomplete
        :autofocus
        :step
        :disabled="disabled"
        :class="{ 'pointer-events-none opacity-50': disabled }"
        @input="$emit('update:modelValue', $event.target.value)"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
      />
      <div v-if="suffix && !pending" class="opacity-50 ml-2 whitespace-nowrap">
        <span class="">{{ suffix }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
input:-webkit-autofill {
  box-shadow: 0 0 0 1000px white inset !important;
  -webkit-box-shadow: 0 0 0 1000px white inset !important;
  -webkit-text-fill-color: #000 !important;
  transition: background-color 5000s ease-in-out 0s;
}
</style>
